import React from 'react';
import { COUNT_CHOICES, countOf } from '../../utils/clientSetup';

/**
 * The controls the setup flow is made of.
 *
 * **No explanatory note under anything (15 Oct 2026).** Every option used
 * to carry a sentence saying what it meant. Tom: *"we don't need all this
 * writing — it is obvious and all it does is clutter the screen."* He is
 * right: "Photography — a gallery rather than a player" tells a producer
 * nothing they did not know from the word Photography.
 *
 * **The one tag that survived sits under its label, not beside it
 * (16 Oct 2026).** *Something else* earns a few words because its name
 * tells you nothing. Inline, it was the only row in the list with a second
 * element and it ran past the right edge of every label above it, so the
 * eye was pulled down to the last item in a list where the top items
 * matter more. On its own line the column edge stays clean and the list
 * keeps its rhythm.
 *
 * `aria-pressed` rather than radio or checkbox roles: these screens are
 * both one-of and any-of, and a filled square reads as chosen either way.
 */
export function SetupChoice({
  label,
  tag,
  pressed,
  onPick
}: {
  label: string;
  /** A few quiet words under the label — "Social cutdowns, live stream". */
  tag?: string;
  pressed: boolean;
  onPick: () => void;
}) {
  return <li className="border-b border-ruleDark first:border-t first:border-ruleDark" data-id="element-1259" data-mp="u3y8s6pdlbk3">
      <button type="button" onClick={onPick} aria-pressed={pressed} className="group flex w-full items-start gap-5 py-5 text-left" data-id="element-1260" data-mp="183zbzst44v2q">
        <span aria-hidden className={['mt-[0.7rem] h-[9px] w-[9px] shrink-0 transition-colors duration-200 ease-out', pressed ? 'bg-paper' : 'bg-paper/20 group-hover:bg-paper/50'].join(' ')} data-id="element-1261" data-mp="1hukh7z3akxum" />
        <span className="min-w-0 flex-1" data-id="element-1262" data-mp="1qvs31homg67t">
          <span className={['block font-display text-[1.7rem] font-light leading-tight tracking-tight transition-colors duration-200 ease-out sm:text-[2rem]', pressed ? 'text-paper' : 'text-paper/55 group-hover:text-paper'].join(' ')} data-id="element-1263" data-mp="1mtna6l5zzdm8">
            {label}
          </span>
          {tag && <span className="label-sm mt-2 block text-mute" data-id="element-1264" data-mp="23u9gaolbmq9h">{tag}</span>}
        </span>
      </button>
    </li>;
}

/**
 * HOW MANY — numbers to hit, and **Ongoing** beside them.
 *
 * The row used to stop at twelve, which read as a ceiling on the work
 * rather than on the buttons. Tom: *"what if it is more or a retainer
 * (on-going series)? That is also a question for films."* Ongoing is not a
 * bigger number, it is a different kind of answer — a retainer has no
 * agreed end — so it sits as its own control and the count becomes how
 * many to start with.
 */
export function SetupCount({
  value,
  ongoing,
  pieces,
  onCount,
  onOngoing
}: {
  value: number;
  ongoing: boolean;
  pieces: string;
  onCount: (count: number) => void;
  onOngoing: (ongoing: boolean) => void;
}) {
  return <div data-id="element-1265" data-mp="3ahnpkgp2aowe">
      <p className="label m-0 text-paper/40" data-id="element-1266" data-mp="3aifdr6gbxkcn">{ongoing ? `How many to start with` : 'How many'}</p>

      <div className="mt-5 flex flex-wrap gap-2.5" data-id="element-1267" data-mp="35okqxu5fgp9c">
        {COUNT_CHOICES.map(count => {
        const on = count === value;
        return <button key={count} type="button" onClick={() => onCount(count)} aria-pressed={on} aria-label={countOf(count, pieces)} className={['flex h-14 w-14 items-center justify-center border font-display text-[1.3rem] font-light leading-none transition-colors duration-200 ease-out', on ? 'border-paper bg-paper text-ink' : 'border-ruleDark text-paper/45 hover:border-paper/60 hover:text-paper'].join(' ')} data-id="element-1268" data-mp="2wpzemgqju6h0">
              {count}
            </button>;
      })}

        <button type="button" onClick={() => onOngoing(!ongoing)} aria-pressed={ongoing} className={['label flex h-14 items-center border px-6 transition-colors duration-200 ease-out', ongoing ? 'border-paper bg-paper text-ink' : 'border-ruleDark text-paper/45 hover:border-paper/60 hover:text-paper'].join(' ')} data-id="element-1269" data-mp="242v5q0oc57zy">
          Ongoing
        </button>
      </div>
    </div>;
}

/** A switch for a detail that has a strong default. */
export function SetupSwitch({
  question,
  options,
  value,
  onPick
}: {
  question: string;
  options: {
    key: string;
    label: string;
  }[];
  value: string;
  onPick: (key: string) => void;
}) {
  return <div className="mt-12" data-id="element-1270" data-mp="ag72rcu6wh6v">
      <p className="label m-0 text-paper/40" data-id="element-1271" data-mp="aff57wh189dy">{question}</p>
      <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-1272" data-mp="2q7l6lbsv3mfr">
        {options.map(option => {
        const on = option.key === value;
        return <button key={option.key} type="button" onClick={() => onPick(option.key)} aria-pressed={on} className={['flex h-14 items-center border px-7 font-display text-[1.05rem] font-light leading-none transition-colors duration-200 ease-out', on ? 'border-paper bg-paper text-ink' : 'border-ruleDark text-paper/45 hover:border-paper/60 hover:text-paper'].join(' ')} data-id="element-1273" data-mp="2v8iorodvggh9">
              {option.label}
            </button>;
      })}
      </div>
    </div>;
}